/**
 * Quote Command Repository
 *
 * Handles all write operations for quotes (create, update, delete).
 * This repository is responsible for modifying quote data in the database.
 */

import { BaseRepository, RepositoryResult } from './BaseRepository';
import { getDbClient } from '@/server/lib/database';
import { Quote } from '@/shared/types';

/**
 * Quote Command Repository
 * Provides write operations for the quotes table
 */
export class QuoteCommandRepository extends BaseRepository<Quote> {
  protected tableName = 'quotes' as const;

  /**
   * Create a new quote with items
   */
  async create(
    userId: string,
    quoteData: Omit<Quote, 'id' | 'createdAt' | 'updatedAt'>,
    quoteNumber: string,
    publicToken?: string
  ): Promise<RepositoryResult<Quote>> {
    try {
      const db = getDbClient();

      // Create the quote
      const { data: quote, error: quoteError } = await db
        .from(this.tableName)
        .insert({
          user_id: userId,
          client_id: quoteData.clientId,
          quote_number: quoteNumber,
          title: quoteData.title,
          description: quoteData.description,
          status: quoteData.status,
          valid_until: quoteData.validUntil,
          subtotal: quoteData.subtotal,
          tax_rate: quoteData.taxRate,
          tax_amount: quoteData.taxAmount,
          total_amount: quoteData.totalAmount,
          notes: quoteData.notes,
          terms: quoteData.terms,
          public_token: publicToken,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (quoteError) {
        throw quoteError;
      }

      // Add items if provided
      if (quoteData.items && quoteData.items.length > 0) {
        const quoteItems = quoteData.items.map((item) => ({
          quote_id: quote.id,
          service_id: item.serviceId,
          description: item.description,
          quantity: item.quantity,
          rate: item.rate,
          total: item.rate * item.quantity,
        }));

        const { error: itemsError } = await db
          .from('quote_items')
          .insert(quoteItems);

        if (itemsError) {
          throw itemsError;
        }
      }

      // Import dynamically to avoid circular dependency
      const { quoteQueryRepository } = await import('./QuoteQueryRepository');

      // Fetch the complete quote with relations
      return quoteQueryRepository.findById(quote.id);
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'creating quote'),
      };
    }
  }

  /**
   * Update an existing quote
   */
  async update(id: string, quoteData: Partial<Quote>): Promise<RepositoryResult<Quote>> {
    try {
      const db = getDbClient();

      const updateData: any = {
        title: quoteData.title,
        description: quoteData.description,
        status: quoteData.status,
        valid_until: quoteData.validUntil,
        subtotal: quoteData.subtotal,
        tax_rate: quoteData.taxRate,
        tax_amount: quoteData.taxAmount,
        total_amount: quoteData.totalAmount,
        notes: quoteData.notes,
        terms: quoteData.terms,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await db
        .from(this.tableName)
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        return {
          success: false,
          error: 'Quote not found',
        };
      }

      // Update items if provided
      if (quoteData.items) {
        // Delete existing quote items
        await db.from('quote_items').delete().eq('quote_id', id);

        // Add new items
        if (quoteData.items.length > 0) {
          const quoteItems = quoteData.items.map((item) => ({
            quote_id: id,
            service_id: item.serviceId,
            description: item.description,
            quantity: item.quantity,
            rate: item.rate,
            total: item.rate * item.quantity,
          }));

          const { error: itemsError } = await db
            .from('quote_items')
            .insert(quoteItems);

          if (itemsError) {
            throw itemsError;
          }
        }
      }

      // Import dynamically to avoid circular dependency
      const { quoteQueryRepository } = await import('./QuoteQueryRepository');

      // Fetch the complete quote with relations
      return quoteQueryRepository.findById(id);
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'updating quote'),
      };
    }
  }

  /**
   * Delete a quote
   */
  async delete(id: string): Promise<RepositoryResult<void>> {
    try {
      const db = getDbClient();

      // Delete quote items first (foreign key constraint)
      await db.from('quote_items').delete().eq('quote_id', id);

      // Delete the quote
      const { error } = await db
        .from(this.tableName)
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      return {
        success: true,
        data: undefined,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'deleting quote'),
      };
    }
  }

  /**
   * Update quote status (shortcut for common operation)
   */
  async updateStatus(id: string, status: Quote['status']): Promise<RepositoryResult<Quote>> {
    return this.update(id, { status });
  }

  /**
   * Bulk update quote statuses
   */
  async bulkUpdateStatus(ids: string[], status: Quote['status']): Promise<RepositoryResult<void>> {
    try {
      const db = getDbClient();

      const { error } = await db
        .from(this.tableName)
        .update({
          status,
          updated_at: new Date().toISOString(),
        })
        .in('id', ids);

      if (error) {
        throw error;
      }

      return {
        success: true,
        data: undefined,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'bulk updating quote statuses'),
      };
    }
  }
}

// Export singleton instance
export const quoteCommandRepository = new QuoteCommandRepository();
